import { apiUrl } from "@/lib/client-environment";
import type { GlobeFrame, GlobeStreamMode, GlobeStreamSource, HotZone, HotZoneCategory, HotZoneTrend } from "@/types/gdelt";

export const DEFAULT_GDELT_MOCK_FRAME_URL = "/mock/gdelt/globe-frame.json";
export const DEFAULT_GDELT_LIVE_FRAME_URL = apiUrl("/api/gdelt/globe-frame");

export const HOT_ZONE_CATEGORY_STYLE: Record<
  HotZoneCategory,
  { label: string; color: [number, number, number]; glow: string }
> = {
  social_unrest: { label: "Social unrest", color: [0.85, 0.19, 0.19], glow: "#d93636" },
  crime: { label: "Crime", color: [0.92, 0.72, 0.24], glow: "#eab83d" },
  corruption: { label: "Corruption", color: [0.08, 0.72, 0.64], glow: "#14b8a6" },
  political_violence: { label: "Political violence", color: [0.95, 0.24, 0.35], glow: "#f43f5e" },
  institutional_instability: { label: "Institutional instability", color: [0.22, 0.74, 0.97], glow: "#38bdf8" },
};

export const EXPORT_SIGNAL_CATEGORY_STYLE: typeof HOT_ZONE_CATEGORY_STYLE = {
  social_unrest: { label: "Social unrest", color: [1, 0.06, 0.04], glow: "#ff100a" },
  crime: { label: "Crime", color: [1, 0.92, 0.02], glow: "#ffeb05" },
  corruption: { label: "Corruption", color: [0.04, 0.25, 1], glow: "#0a40ff" },
  political_violence: { label: "Political violence", color: [1, 0.02, 0.42], glow: "#ff056b" },
  institutional_instability: { label: "Institutional instability", color: [0.02, 0.95, 1], glow: "#05f2ff" },
};

export const HOT_ZONE_CATEGORIES = Object.keys(HOT_ZONE_CATEGORY_STYLE) as HotZoneCategory[];

export const GEO_PRESSURE_TIME_WINDOWS = [
  { label: "15m", minutes: 15 },
  { label: "1h", minutes: 60 },
  { label: "6h", minutes: 360 },
  { label: "24h", minutes: 1440 },
] as const;

export type GeoPressureFilterState = {
  category: HotZoneCategory | "all";
  tWindowMinutes: number;
  minIntensity: number;
  zoneId: string | null;
};

export type GlobeCameraUrlState = {
  alpha: number;
  beta: number;
  radius: number;
  positionX: number;
  positionY: number;
  positionZ: number;
  targetX: number;
  targetY: number;
  targetZ: number;
};

export const HOT_ZONE_TREND_MULTIPLIER: Record<HotZoneTrend, number> = {
  rising: 1.25,
  stable: 1,
  falling: 0.75,
};

export type CartesianPoint = {
  x: number;
  y: number;
  z: number;
};

export function latLonToCartesian(lat: number, lon: number, radius = 1): CartesianPoint {
  const latRad = degreesToRadians(lat);
  const lonRad = degreesToRadians(lon);
  const cosLat = Math.cos(latRad);

  return {
    x: radius * cosLat * Math.sin(lonRad),
    y: radius * Math.sin(latRad),
    z: radius * cosLat * Math.cos(lonRad),
  };
}

export function getHotZoneVisualWeight(zone: Pick<HotZone, "intensity" | "trend">) {
  const normalizedIntensity = clamp(zone.intensity / 100, 0.05, 1);
  return normalizedIntensity * HOT_ZONE_TREND_MULTIPLIER[zone.trend];
}

export function getHotZonePulseSpeed(zone: Pick<HotZone, "trend" | "intensity">) {
  const base = zone.trend === "rising" ? 1.6 : zone.trend === "falling" ? 0.7 : 1;
  return base + clamp(zone.intensity / 180, 0, 0.55);
}

export function resolveGlobeStreamSource(
  rawSource?: string | null,
  fallbackUrl = DEFAULT_GDELT_MOCK_FRAME_URL,
): GlobeStreamSource {
  const source = rawSource?.trim() || fallbackUrl;
  return {
    url: source,
    mode: inferStreamMode(source),
  };
}

export function inferStreamMode(url: string): GlobeStreamMode {
  if (url.startsWith("ws://") || url.startsWith("wss://")) return "websocket";
  if (url.endsWith(".json") || url.includes("/mock/")) return "mock";
  if (url.includes("/api/gdelt/")) return "polling";
  return "eventsource";
}

export function getStreamSourceFromSearchParams(searchParams: Record<string, string | string[] | undefined>) {
  const source = searchParams.source;
  return Array.isArray(source) ? source[0] : source;
}

export function isHotZoneCategory(value: unknown): value is HotZoneCategory {
  return typeof value === "string" && HOT_ZONE_CATEGORIES.includes(value as HotZoneCategory);
}

export function parseGeoPressureFilters(searchParams: Record<string, string | string[] | undefined>): GeoPressureFilterState {
  const rawCategory = firstSearchParam(searchParams.category);
  const rawWindow = Number(firstSearchParam(searchParams.t_window));
  const rawIntensity = Number(firstSearchParam(searchParams.min_intensity));
  const zoneId = firstSearchParam(searchParams.zone);

  return {
    category: isHotZoneCategory(rawCategory) ? rawCategory : "all",
    tWindowMinutes: normalizeTimeWindow(rawWindow),
    minIntensity: Number.isFinite(rawIntensity) ? clamp(Math.round(rawIntensity), 0, 100) : 0,
    zoneId: zoneId || null,
  };
}

export function filterGlobeFrame(frame: GlobeFrame, filters: Pick<GeoPressureFilterState, "category" | "minIntensity">): GlobeFrame {
  const zones = frame.zones.filter((zone) => {
    const matchesCategory = filters.category === "all" || zone.category === filters.category;
    return matchesCategory && zone.intensity >= filters.minIntensity;
  });

  const category_breakdown = zones.reduce<GlobeFrame["category_breakdown"]>(
    (breakdown, zone) => {
      breakdown[zone.category] = (breakdown[zone.category] ?? 0) + zone.event_count;
      return breakdown;
    },
    {
      social_unrest: 0,
      crime: 0,
      corruption: 0,
      political_violence: 0,
      institutional_instability: 0,
    },
  );

  return {
    ...frame,
    category_breakdown,
    zones,
  };
}

export function buildGeoPressureIntelligenceHref(
  pathname: string,
  filters: GeoPressureFilterState,
  extras: Record<string, string | null | undefined> = {},
) {
  const params = new URLSearchParams();

  if (filters.zoneId) params.set("zone", filters.zoneId);
  params.set("t_window", String(filters.tWindowMinutes));
  if (filters.category !== "all") params.set("category", filters.category);
  if (filters.minIntensity > 0) params.set("min_intensity", String(filters.minIntensity));

  Object.entries(extras).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function parseGlobeCameraUrlState(searchParams: Record<string, string | string[] | undefined>): GlobeCameraUrlState | null {
  const camera = {
    alpha: Number(firstSearchParam(searchParams.cam_alpha)),
    beta: Number(firstSearchParam(searchParams.cam_beta)),
    radius: Number(firstSearchParam(searchParams.cam_radius)),
    positionX: Number(firstSearchParam(searchParams.cam_px)),
    positionY: Number(firstSearchParam(searchParams.cam_py)),
    positionZ: Number(firstSearchParam(searchParams.cam_pz)),
    targetX: Number(firstSearchParam(searchParams.cam_tx)),
    targetY: Number(firstSearchParam(searchParams.cam_ty)),
    targetZ: Number(firstSearchParam(searchParams.cam_tz)),
  };

  return Object.values(camera).every(Number.isFinite) ? camera : null;
}

export function globeCameraStateToSearchParams(
  camera: GlobeCameraUrlState | null | undefined,
  precision = 3,
): Record<string, string | null> {
  if (!camera) {
    return {
      cam_alpha: null,
      cam_beta: null,
      cam_radius: null,
      cam_px: null,
      cam_py: null,
      cam_pz: null,
      cam_tx: null,
      cam_ty: null,
      cam_tz: null,
    };
  }

  return {
    cam_alpha: formatCameraNumber(camera.alpha, precision),
    cam_beta: formatCameraNumber(camera.beta, precision),
    cam_radius: formatCameraNumber(camera.radius, precision),
    cam_px: formatCameraNumber(camera.positionX, precision),
    cam_py: formatCameraNumber(camera.positionY, precision),
    cam_pz: formatCameraNumber(camera.positionZ, precision),
    cam_tx: formatCameraNumber(camera.targetX, precision),
    cam_ty: formatCameraNumber(camera.targetY, precision),
    cam_tz: formatCameraNumber(camera.targetZ, precision),
  };
}

function formatCameraNumber(value: number, precision: number) {
  return Number(value.toFixed(precision)).toString();
}

function normalizeTimeWindow(minutes: number) {
  const fallback = GEO_PRESSURE_TIME_WINDOWS[0].minutes;
  if (!Number.isFinite(minutes)) return fallback;
  return GEO_PRESSURE_TIME_WINDOWS.some((window) => window.minutes === minutes) ? minutes : fallback;
}

function firstSearchParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function degreesToRadians(value: number) {
  return (value * Math.PI) / 180;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
